import Button from '@mui/material/Button'
import Icon from '@mui/material/Icon'
import Menu from '@mui/material/Menu'
import Tooltip from '@mui/material/Tooltip'
import React from 'react'

import MenuItem from '@/app/component/atom/menu-item'
import Modal from '@/app/component/layout/modal'
import { NO_LIBRARY_MESSAGES_TOOLTIP, RUNNING_CAMPAIGNS_DISABLED_TOOLTIP } from '@/app/module/campaigns/definitions'
import { CampaignTemplateType, ReminderMessage, Message as TMessage } from '@/app/module/campaigns/types'
import { SelectOption } from '@/app/types'

import { ContentTypeIcon } from './content-type-icon'
import { useMessageSnippets } from './hooks/use-message-snippets'
import { MessageLibrary } from './message-library'
import { SUPPORTED_LIBRARY_MESSAGE_TYPES } from './message-snippet'
import { getEmptyMessage } from './utils/empty-message'

const buttonStyle = {
  gap: 2,
  minWidth: '0',
  padding: '8px',
}

type MessageActionsProps = {
  index: number
  campaignType: CampaignTemplateType
  total: number
  published: boolean
  messageIds: string[]
  hideDuplicate?: boolean
  supportedMessageTypes: SelectOption<string>[]
  timezone: string

  onMessageDuplicate: () => void
  onMessageCreate: (message: TMessage | ReminderMessage) => void
}

export function MessageActions({
  index,
  campaignType,
  total,
  published,
  timezone,
  messageIds,
  hideDuplicate = false,
  supportedMessageTypes,
  onMessageCreate,
  onMessageDuplicate,
}: MessageActionsProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [isSnippetModalOpen, setSnippetModalOpen] = React.useState(false)
  const { data: messageSnippets } = useMessageSnippets({ supportedMessageTypes })

  const supportedTypeValues = React.useMemo(() => supportedMessageTypes.map((o) => o.value), [supportedMessageTypes])
  const isLibrarySupported = React.useMemo(() => {
    return supportedTypeValues.some((type) => SUPPORTED_LIBRARY_MESSAGE_TYPES.includes(type))
  }, [supportedTypeValues])

  const open = Boolean(anchorEl)
  const actionDisabled =
    published &&
    index < total - 1 &&
    campaignType !== CampaignTemplateType.CATI &&
    campaignType !== CampaignTemplateType.VoiceSurvey
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const isLibraryDisabled = actionDisabled || messageSnippets.length === 0
  const libraryTooltip = React.useMemo(() => {
    if (actionDisabled) {
      return RUNNING_CAMPAIGNS_DISABLED_TOOLTIP
    }
    return messageSnippets.length > 0 ? undefined : NO_LIBRARY_MESSAGES_TOOLTIP
  }, [actionDisabled, messageSnippets.length])

  return (
    <div
      style={{
        display: 'flex',
        gap: 4,
        marginLeft: 'auto',
        textAlign: 'right',
      }}
    >
      {!hideDuplicate && (
        <Tooltip title={actionDisabled ? RUNNING_CAMPAIGNS_DISABLED_TOOLTIP : undefined}>
          <span>
            <Button
              id={`duplicate-part-${index}`}
              className="campaign-item-content-dup-button"
              disabled={actionDisabled}
              variant="contained"
              color="primary"
              style={buttonStyle}
              startIcon={<Icon>content_copy</Icon>}
              onClick={() => onMessageDuplicate()}
            >
              Duplicate part
            </Button>
          </span>
        </Tooltip>
      )}
      <Tooltip title={actionDisabled ? RUNNING_CAMPAIGNS_DISABLED_TOOLTIP : undefined}>
        <span>
          <Button
            id={`message-add-button-${index}`}
            className="campaign-item-content-add-button"
            disabled={actionDisabled}
            variant="contained"
            color="primary"
            style={buttonStyle}
            startIcon={<Icon>add</Icon>}
            endIcon={<Icon>arrow_drop_down</Icon>}
            onClick={handleMenuOpen}
          >
            Add part
          </Button>
          <Menu
            id={`message-add-menu-${index}`}
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            MenuListProps={{
              'aria-labelledby': `message-add-button-${index}`,
            }}
          >
            {isLibrarySupported && (
              <MenuItem
                divider
                disabled={isLibraryDisabled}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                tooltip={libraryTooltip}
                onClick={() => {
                  if (isLibraryDisabled) {
                    return
                  }
                  handleMenuClose()
                  setSnippetModalOpen(true)
                }}
              >
                <Icon>library_add</Icon>
                <span>Add from library</span>
              </MenuItem>
            )}
            {supportedMessageTypes.map((type) => (
              <MenuItem
                disabled={type.disabled}
                key={type.value}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                tooltip={type.disabled ? type.disabledReason : undefined}
                onClick={() => {
                  if (type.disabled) {
                    return
                  }
                  const message = getEmptyMessage(campaignType, type.value, timezone, index + 1)
                  onMessageCreate(message)
                  handleMenuClose()
                }}
              >
                <ContentTypeIcon type={type.value} />
                <span>{type.label}</span>
              </MenuItem>
            ))}
          </Menu>
        </span>
      </Tooltip>

      <Modal
        title="Add from library"
        icon="library_add"
        isOpen={isSnippetModalOpen}
        onClose={() => setSnippetModalOpen(false)}
      >
        <MessageLibrary
          campaignType={campaignType}
          snippets={messageSnippets}
          messageIds={messageIds}
          insertionIndex={index + 1}
          timezone={timezone}
          onSelect={(snippet) => {
            onMessageCreate(snippet)
            setSnippetModalOpen(false)
          }}
        />
      </Modal>
    </div>
  )
}

export type { MessageActionsProps }
